/**
 * Everything the first-claim reveal shows for one claim, read from the same
 * endpoints the claim view uses (claim, communications, documents, extracted
 * deadlines). Each part loads on its own: a failed list shows as empty rather
 * than hiding the claim.
 */
import { useQuery } from '@tanstack/react-query';
import type {
  ExtractedDeadlineSummary,
  FirstClaimDocument,
  FirstClaimEmail,
  FirstClaimFields,
} from '@/lib/first-claim-next-steps';

export interface FirstClaimSummary {
  claim: (FirstClaimFields & { id: string }) | null;
  emails: FirstClaimEmail[];
  documents: FirstClaimDocument[];
  pendingDeadlines: ExtractedDeadlineSummary[];
  isLoading: boolean;
  /** The claim itself could not be read (for example, no access). */
  isError: boolean;
}

export function useFirstClaimSummary(claimId: string | undefined): FirstClaimSummary {
  const enabled = Boolean(claimId);
  const claim = useQuery<FirstClaimFields & { id: string }>({ queryKey: [`/api/claims/${claimId}`], enabled });
  const communications = useQuery<Array<FirstClaimEmail & { type?: string }>>({
    queryKey: [`/api/claims/${claimId}/communications`],
    enabled,
  });
  const documents = useQuery<FirstClaimDocument[]>({ queryKey: [`/api/claims/${claimId}/documents`], enabled });
  const deadlines = useQuery<ExtractedDeadlineSummary[]>({
    queryKey: [`/api/claims/${claimId}/extracted-deadlines`, { status: 'pending' }],
    enabled,
  });

  const emails = (communications.data ?? []).filter((item) => !item.type || item.type === 'email');

  return {
    claim: claim.data ?? null,
    emails,
    documents: documents.data ?? [],
    pendingDeadlines: deadlines.data ?? [],
    isLoading: claim.isLoading,
    isError: claim.isError,
  };
}
